import { useEffect, useMemo, useState } from "react";
import { Check, Clock } from "lucide-react";
import type { AddablePersona } from "@/entities/persona";

interface PersonaPickerProps {
  addable: AddablePersona[];
  onPick: (roleSlug: string) => void;
}

/** Role catalog panel: verticals as filter pills, roles as rows.
 *  Roles whose persona already has reports are shown disabled — picking them
 *  would be a no-op switch, and the strip already offers that.
 *
 *  Pure content: it renders inside a Popover, which owns positioning, portalling,
 *  collision handling, focus, Escape and click-outside. */
export function PersonaPicker({ addable, onPick }: PersonaPickerProps) {
  const verticals = useMemo(() => {
    const seen: string[] = [];
    for (const role of addable) {
      if (role.vertical && !seen.includes(role.vertical)) seen.push(role.vertical);
    }
    return seen;
  }, [addable]);

  const [vertical, setVertical] = useState<string>(verticals[0] ?? "");

  useEffect(() => {
    if (verticals.length > 0 && !verticals.includes(vertical)) setVertical(verticals[0]!);
  }, [verticals, vertical]);

  const rows = addable.filter((role) => role.vertical === vertical);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium px-0.5">Add persona</p>

      <div className="flex flex-wrap gap-1">
        {verticals.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVertical(v)}
            className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
              v === vertical
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {v}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-0.5 max-h-[210px] overflow-y-auto">
        {rows.map((role) => (
          <button
            key={role.role_slug}
            type="button"
            disabled={role.generated}
            onClick={() => onPick(role.role_slug)}
            className="w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-md text-sm text-left transition-colors enabled:hover:bg-muted disabled:text-muted-foreground disabled:cursor-default"
          >
            <span className="truncate">{role.role_name}</span>
            {role.generated ? (
              <span className="inline-flex items-center gap-1 text-[11px] shrink-0">
                <Check className="w-3 h-3" />
                Added
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground shrink-0">~2 min</span>
            )}
          </button>
        ))}
        {rows.length === 0 && (
          <p className="px-2 py-3 text-xs text-muted-foreground">No roles in this group.</p>
        )}
      </div>

      <p className="flex items-start gap-1.5 border-t pt-2 text-[11px] text-muted-foreground leading-snug">
        <Clock className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          Reports for a new persona are generated <strong className="font-medium text-foreground">once</strong>.
          The transcript and identified speakers are reused — nothing is re-transcribed.
        </span>
      </p>
    </div>
  );
}
